import getSlug from 'speakingurl';

import chroma from 'chroma-js';
import { customAlphabet } from 'nanoid';
import { v4 as uuid_v4 } from 'uuid';

import { addedDiff, deletedDiff, detailedDiff, diff, updatedDiff } from 'deep-object-diff';
import { Base64 } from 'js-base64';
import { SlugNames } from './config/constants';

export const base64 = Base64;

export const uuid = uuid_v4;

export const objectDiff = {
  diff,
  addedDiff,
  deletedDiff,
  updatedDiff,
  detailedDiff,
};

export const isArray = (obj) => typeof obj === 'object' && Array.isArray(obj);

export const isObject = (obj) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null;

export const cloneObject = <T>(obj: T): T => (obj !== undefined ? JSON.parse(JSON.stringify(obj)) : undefined);

export const mergeObject = (target, source) => {
  if (!isObject(target) || !isObject(source)) {
    return source;
  }

  for (const key of Object.keys(source)) {
    const targetValue = target[key];
    const sourceValue = source[key];

    if (isArray(targetValue) && isArray(sourceValue)) {
      target[key] = targetValue.concat(sourceValue);
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      target[key] = mergeObject(Object.assign({}, targetValue), sourceValue);
    } else {
      target[key] = sourceValue;
    }
  }

  return target;
};

export const unique = <T>(array: T[]) => Array.from(new Set(array)); // array.filter((obj, idx, nodes) => nodes.indexOf(obj) === idx);

export const uniqueSet = <T>(array: T[]) => Array.from(new Set(array)); // [...new Set(array)];

export const uniqueFilter = <T>(array: T[], mapFn: (obj: T) => string) => {
  const map = new Map<string, T>();
  for (const obj of array) {
    map.set(mapFn(obj), obj);
  }

  return Array.from(map.values());
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const sleepWith = (data, ms) => new Promise((resolve) => setTimeout(() => resolve(data), ms));

export const groupBy = (array, key) => {
  return array.reduce((acc, obj) => {
    (acc[obj[key]] = acc[obj[key]] || []).push(obj);
    return acc;
  }, {});
};

export const flatten = (array: any[]) => array.reduce((acc, obj) => acc.concat(Array.isArray(obj) ? flatten(obj) : obj), []);

export const shuffle = (array: any[]) => array.sort(() => Math.random() - 0.5);

export const clamp = (num: number, min: number, max: number) => Math.min(Math.max(min, num), max);

export const roundTo = (num: number, digits = 2) => Math.round(num * Math.pow(10, digits)) / Math.pow(10, digits);

export const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

export const deg2rad = (angle: number) => angle * (Math.PI / 180);

export const rad2deg = (radians: number) => radians * (180 / Math.PI);

export const range = (start: number, end: number, step: number = 1) => [...Array(Math.floor((end - start + 1) / step)).keys()].map((num) => start + num * step);

export type DFPredicate = (value: any, key: string, parent: object | []) => boolean;
export type DFOptions = { path?: boolean; seperator?: string };
export type DFReducerEntry = [string, any, DFPredicate, string[], object | []];
export type DFReducerReturn = [string[], any][];

export const deepFilterReducer = (accumulator: DFReducerReturn, entry: DFReducerEntry) => {
  const [key, value, filter, path, parent] = entry;
  if (filter(value, key, parent)) accumulator.push([path, value]);
  if (isArray(value) || isObject(value)) accumulator = accumulator.concat(deepFilterEntries(value, filter, path));
  return accumulator;
};

export const deepFilterEntries = (obj: object | [], filter: DFPredicate, path: string[] = []) =>
  Object.entries(obj)
    .map<DFReducerEntry>(([key, value]) => [key, value, filter, path.concat(key), obj])
    .reduce(deepFilterReducer, []);

export const deepFilter = <T>(obj: object | [], filter: DFPredicate, options: DFOptions = {}): T[] =>
  deepFilterEntries(obj, filter).map(([path, value]) => (options.path ? [path.join(options.seperator ?? '.'), value] : value));

// hex transitions suck, alpha would need svg fill #rrggbb and fill-opacity #a
export const color2css = (color) => chroma(color).hex();

export const transpose = (matrix: any[][]) => {
  const rows = matrix.length;
  const cols = matrix[0].length;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < row; col++) {
      [matrix[row][col], matrix[col][row]] = [matrix[col][row], matrix[row][col]];
    }
  }

  // return matrix[0].map((col, colIdx) => matrix.map((row) => row[colIdx]).reverse());
};

const reverse = (array: any[]) => [...array].reverse();
const compose = (a, b) => (x) => a(b(x));

const flipMatrix = (matrix: any[][]) => matrix[0].map((column, index) => matrix.map((row) => row[index]));

const rotateMatrix = compose(flipMatrix, reverse);
const flipMatrixCounterClockwise = compose(reverse, rotateMatrix);
const rotateMatrixCounterClockwise = compose(reverse, flipMatrix);

export const debounce = (callback, wait, immediate = false) => {
  let timeout;

  return function (...args) {
    // @ts-ignore
    const context = this;
    const callNow = immediate && !timeout;
    const next = () => callback.apply(context, args);

    clearTimeout(timeout);
    timeout = setTimeout(next, wait);

    if (callNow) {
      next();
    }
  };
};

const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const hashId = customAlphabet(alphabet, 16);

export const createHashSlug = (name) => {
  const nameSlug = getSlug(name, { truncate: 32 });
  const fullSlug = `${hashId()}-${nameSlug}`;
  return fullSlug;
};

export const createContentHashSlug = (instance) => {
  const contentName = instance.get('fileName') || instance.get('name');
  const contentType = instance.get('type');

  const nameSlug = getSlug(contentName, { truncate: 32 });
  const niceType = SlugNames[contentType] || 'unknown';

  const fullSlug = `${hashId()}-${niceType}-${nameSlug}`;
  return fullSlug;
};

export const createTagHashSlug = (instance) => {
  const contentName = instance.get('label');
  const nameSlug = getSlug(contentName, { truncate: 32 });

  const fullSlug = `${nameSlug}-${hashId()}`;
  return fullSlug;
};

// Replace all 23degrees.io strings with 23degrees.eu in a string
export const replaceAllIOWithEU = (text: string) => {
  const searchRegExp = /23degrees\.io/g;
  const replaceWith = '23degrees.eu';
  return text.replace(searchRegExp, replaceWith);
};

// Replace all 23degrees.eu strings with 23degrees.io in a string
export const replaceAllEUWithIO = (text: string) => {
  const searchRegExp = /23degrees\.eu/g;
  const replaceWith = '23degrees.io';
  return text.replace(searchRegExp, replaceWith);
};

/**
 * Convert any string to a color
 * reduced makes take color almost transparent
 */
export const stringToColour = (str: string, reduced: boolean = true) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let colour = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    colour += `00${value.toString(16)}`.substr(-2);
  }
  if (reduced) {
    return chroma(colour).desaturate(2).css();
  }
  return chroma(colour).css();
};

export const pastelColourFromString = (input_str: string, reduce: boolean = false) => {
  const baseRed = 128;
  const baseGreen = 128;
  const baseBlue = 128;

  //lazy seeded random hack to get values from 0 - 256
  //for seed just take bitwise XOR of first two chars
  let seed = input_str.charCodeAt(0) ^ input_str.charCodeAt(1);
  const rand_1 = Math.abs(Math.sin(seed++) * 10000) % 256;
  const rand_2 = Math.abs(Math.sin(seed++) * 10000) % 256;
  const rand_3 = Math.abs(Math.sin(seed++) * 10000) % 256;

  //build colour
  const red = Math.round((rand_1 + baseRed) / 2);
  const green = Math.round((rand_2 + baseGreen) / 2);
  const blue = Math.round((rand_3 + baseBlue) / 2);

  return chroma([red, green, blue]).css();
};

export const contrastColorFromString = (string: string, reduced: boolean = true) => {
  const lightColor = '#FFFFFF';
  const darkColor = '#FFFFFF';
  const color = pastelColourFromString(string, reduced);
  return chroma.contrast(color, darkColor) > chroma.contrast(color, lightColor) ? darkColor : lightColor;
};

export const isNullOrUndefined = (value: any): boolean => {
  return typeof value === 'undefined' || value === null || value === undefined;
};

export const multiIndexOf = (array: any[], value: any): number[] => {
  const idxs: number[] = [];
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i] === value) {
      idxs.unshift(i);
    }
  }
  return idxs;
};
